import { Trash2, Plus } from "@/components/icons";
import type { RateRule, FeeType, Uom } from "@/lib/api";
import {
  type PricingFormValues, UOM_BY_FEE, RATE_CURRENCY_SYMBOL, newRule,
} from "@/lib/pricingForm";
import { useT } from "@/app/localization";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Index = the stored day number (Sun=0 .. Sat=6); the code selects the label.
const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

function daysToSet(csv?: string): Set<number> {
  if (!csv) return new Set();
  return new Set(csv.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n)));
}
function setToDays(s: Set<number>): string {
  return [...s].sort((a, b) => a - b).join(",");
}

/**
 * The ONE shared pricing-rule editor. Controlled (value + onChange). Used identically by
 * the charger default-tariff editor and the Pricing Profiles create/edit form — same
 * fields (currency; per-rule fee type charging/idle/activation, unit, rate, optional
 * time-of-use window with days + priority), same validation/serialization (lib/pricingForm).
 * The parent owns saving; this component renders nothing about WHERE it is saved.
 */
export function PricingForm({
  value, onChange, idPrefix = "pricing",
}: {
  value: PricingFormValues;
  onChange: (v: PricingFormValues) => void;
  idPrefix?: string;
}) {
  const t = useT();
  const { rules, currency } = value;
  const setRules = (rules: RateRule[]) => onChange({ ...value, rules });

  const updateRule = (i: number, patch: Partial<RateRule>) =>
    setRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const changeFeeType = (i: number, fee: FeeType) =>
    updateRule(i, { fee_type: fee, uom: UOM_BY_FEE[fee][0] });
  const addRule = () => setRules([...rules, newRule()]);
  const removeRule = (i: number) => setRules(rules.filter((_, idx) => idx !== i));
  const toggleDay = (i: number, day: number) => {
    const s = daysToSet(rules[i].days);
    if (s.has(day)) s.delete(day); else s.add(day);
    updateRule(i, { days: setToDays(s) });
  };
  const applyFreeWindowPreset = () =>
    setRules([
      { fee_type: "charging", uom: "kwh", rate_base: 0.3, is_tou: false, start_time: "", end_time: "", days: "", priority: 0 },
      { fee_type: "charging", uom: "kwh", rate_base: 0, is_tou: true, start_time: "09:00", end_time: "18:00", days: "", priority: 10 },
    ]);

  const isFree = !rules.some((r) => (Number(r.rate_base) || 0) > 0);

  return (
    <div className="space-y-3" data-testid="pricing-form">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label className="text-xs">{t("billing.tariffs.currency_label")}</Label>
          <Input className="mt-1 w-24" data-testid="pricing-currency" value={currency}
            onChange={(e) => onChange({ ...value, currency: e.target.value })} placeholder="USD" />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isFree ? "success" : "neutral"} className="h-6 px-2.5 text-sm">
            {t("billing.tariffs.mode_badge", { mode: isFree ? "free" : "paid" })}
          </Badge>
          <Button type="button" variant="ghost" size="sm" onClick={applyFreeWindowPreset}>
            {t("billing.tariffs.preset_free_window")}
          </Button>
        </div>
      </div>

      {rules.length === 0 && (
        <div className="text-sm opacity-60" data-testid="pricing-empty">
          {t("billing.tariffs.empty")}
        </div>
      )}

      <div className="space-y-3">
        {rules.map((rule, i) => {
          const uoms = UOM_BY_FEE[rule.fee_type];
          const daySet = daysToSet(rule.days);
          return (
            <div key={i} className="rounded-md border p-3 space-y-3" data-testid={`pricing-rule-${i}`}>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs">{t("billing.tariffs.fee_type_label")}</Label>
                  <Select value={rule.fee_type} onValueChange={(v) => changeFeeType(i, v as FeeType)}>
                    <SelectTrigger className="mt-1 w-32" data-testid={`pricing-feetype-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="charging">{t("billing.tariffs.fee_type", { fee: "charging" })}</SelectItem>
                      <SelectItem value="idle">{t("billing.tariffs.fee_type", { fee: "idle" })}</SelectItem>
                      <SelectItem value="activation">{t("billing.tariffs.fee_type", { fee: "activation" })}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("billing.tariffs.unit_label")}</Label>
                  <Select value={rule.uom} onValueChange={(v) => updateRule(i, { uom: v as Uom })}
                    disabled={uoms.length === 1}>
                    <SelectTrigger className="mt-1 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {uoms.map((u) => <SelectItem key={u} value={u}>{t("billing.tariffs.uom", { uom: u })}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("billing.tariffs.rate_label", { uom: rule.uom, currency: RATE_CURRENCY_SYMBOL })}</Label>
                  <Input
                    className={`mt-1 w-28 ${Number(rule.rate_base) === 0 ? "placeholder:text-emerald-600 placeholder:font-medium" : ""}`}
                    data-testid={`pricing-rate-${i}`}
                    type="number" step="0.01" min="0" placeholder={t("billing.tariffs.rate_free_placeholder")}
                    value={Number(rule.rate_base) === 0 ? "" : rule.rate_base}
                    onChange={(e) => updateRule(i, { rate_base: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Switch checked={!!rule.is_tou} onCheckedChange={(c) => updateRule(i, { is_tou: c })} />
                  <Label className="text-xs">{t("billing.tariffs.time_window")}</Label>
                </div>
                <Button type="button" variant="ghost" size="icon" className="ml-auto" onClick={() => removeRule(i)}
                  aria-label={t("billing.tariffs.remove_rule")}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {rule.is_tou && (
                <div className="flex flex-wrap items-end gap-3 border-t pt-3">
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.window_from")}</Label>
                    <Input className="mt-1 w-36 min-w-36" type="time" value={rule.start_time || ""}
                      onChange={(e) => updateRule(i, { start_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.window_to")}</Label>
                    <Input className="mt-1 w-36 min-w-36" type="time" value={rule.end_time || ""}
                      onChange={(e) => updateRule(i, { end_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.days_label")}</Label>
                    <div className="mt-1 flex gap-1">
                      {DAYS.map((day, d) => (
                        <button key={d} type="button" onClick={() => toggleDay(i, d)}
                          aria-label={t("billing.tariffs.day_name", { day })}
                          className={`h-7 w-7 rounded text-xs border ${daySet.has(d) ? "bg-primary text-primary-foreground" : "bg-transparent opacity-70"}`}>
                          {t("billing.tariffs.day_short", { day })}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.priority_label")}</Label>
                    <Input className="mt-1 w-20" type="number" step="1" value={rule.priority ?? 0}
                      onChange={(e) => updateRule(i, { priority: parseInt(e.target.value, 10) || 0 })} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRule} data-testid="pricing-add-rule">
        <Plus className="h-4 w-4 mr-1" /> {t("billing.tariffs.add_rule")}
      </Button>
      <p className="text-xs opacity-50">
        {t("billing.tariffs.rules_help")}
      </p>
    </div>
  );
}
